/**
 * Rebuilds the `glyf` and `loca` tables of a font from the transformed `glyf` table of a WOFF2 file
 * (W3C WOFF 2.0, §5.1 "Transformed glyf table format" and §5.2 "Triplet encoding"). The transform
 * splits each glyph over seven streams: the contour counts, the point counts, the point flags, the
 * coordinates, the composite records, the bounding boxes and the instructions.
 */
import { ByteReader } from '../../../../resources/formats/binaryresource/byteReader';
import { COMPONENT_HAS_INSTRUCTIONS, COMPONENT_MORE, LONG_LOCA_FORMAT, SHORT_LOCA_FORMAT, SIMPLE_ON_CURVE, SIMPLE_X_SAME_OR_POSITIVE, SIMPLE_X_SHORT, SIMPLE_Y_SAME_OR_POSITIVE, SIMPLE_Y_SHORT, componentArgumentByteCount, componentMatrixByteCount, glyphXMin, } from './glyfFormat';
const HEADER_STREAM_COUNT = 7;
const COMPOSITE_CONTOUR_COUNT = -1;
const GLYPH_INDEX_BYTES = 2;
const MAX_SHORT_DELTA = 255;
/** `255UInt16`'s first byte: 253 is followed by a `UInt16`, 254 and 255 by one byte added to their base. */
const WORD_CODE = 253;
const ONE_MORE_BYTE_CODE_2 = 254;
const ONE_MORE_BYTE_CODE_1 = 255;
const ONE_MORE_BYTE_BASE_2 = 506;
/** The triplet flag byte: bit 7 set is an off-curve point, bits 0-6 pick the coordinate encoding (§5.2). */
const TRIPLET_OFF_CURVE = 0x80;
const TRIPLET_ENCODING_MASK = 0x7f;
/**
 * A long `loca` keeps its glyphs on four-byte boundaries, as the reference decoder writes them. A
 * short one keeps them on two, so the padding cannot push a font past the bytes its offsets reach.
 */
const LONG_GLYPH_ALIGNMENT = 4;
const SHORT_GLYPH_ALIGNMENT = 2;
/** A short `loca` holds `offset / 2` in a `UInt16`. */
const MAX_SHORT_LOCA_OFFSET = 0xffff * 2;
/** The bytes of a glyph under construction, in a buffer that doubles when it fills. */
class GlyphBytes {
    data = new Uint8Array(64);
    length = 0;
    u8(value) {
        this.reserve(1);
        this.data[this.length++] = value & 0xff;
    }
    u16(value) {
        this.u8(value >> 8);
        this.u8(value);
    }
    append(bytes) {
        this.reserve(bytes.length);
        this.data.set(bytes, this.length);
        this.length += bytes.length;
    }
    toBytes() {
        return this.data.slice(0, this.length);
    }
    reserve(count) {
        if (this.length + count <= this.data.length)
            return;
        const grown = new Uint8Array(Math.max(this.data.length * 2, this.length + count));
        grown.set(this.data);
        this.data = grown;
    }
}
/** `255UInt16` (§"255UInt16 Data Type"). */
function read255UInt16(reader) {
    const code = reader.u8();
    switch (code) {
        case WORD_CODE:
            return reader.u16();
        case ONE_MORE_BYTE_CODE_1:
            return WORD_CODE + reader.u8();
        case ONE_MORE_BYTE_CODE_2:
            return ONE_MORE_BYTE_BASE_2 + reader.u8();
        default:
            return code;
    }
}
const signed = (flag, magnitude) => (flag & 1 ? magnitude : -magnitude);
/** One point's `dx` and `dy` from the triplet encoding of §5.2, reading its data bytes from `reader`. */
function readTriplet(encoding, reader) {
    if (encoding < 10) {
        return { dx: 0, dy: signed(encoding, ((encoding & 14) << 7) + reader.u8()) };
    }
    if (encoding < 20) {
        return { dx: signed(encoding, (((encoding - 10) & 14) << 7) + reader.u8()), dy: 0 };
    }
    if (encoding < 84) {
        const high = encoding - 20;
        const packed = reader.u8();
        return {
            dx: signed(encoding, 1 + (high & 0x30) + (packed >> 4)),
            dy: signed(encoding >> 1, 1 + ((high & 0x0c) << 2) + (packed & 0x0f)),
        };
    }
    if (encoding < 120) {
        const high = encoding - 84;
        const x = reader.u8();
        const y = reader.u8();
        return {
            dx: signed(encoding, 1 + (Math.floor(high / 12) << 8) + x),
            dy: signed(encoding >> 1, 1 + (((high % 12) >> 2) << 8) + y),
        };
    }
    if (encoding < 124) {
        const x = reader.u8();
        const shared = reader.u8();
        const y = reader.u8();
        return {
            dx: signed(encoding, (x << 4) + (shared >> 4)),
            dy: signed(encoding >> 1, ((shared & 0x0f) << 8) + y),
        };
    }
    const x = reader.u16();
    const y = reader.u16();
    return { dx: signed(encoding, x), dy: signed(encoding >> 1, y) };
}
function readPoints(count, flags, glyphStream) {
    return Array.from({ length: count }, () => {
        const flag = flags.u8();
        return {
            isOnCurve: (flag & TRIPLET_OFF_CURVE) === 0,
            ...readTriplet(flag & TRIPLET_ENCODING_MASK, glyphStream),
        };
    });
}
/** The box of the points, which a glyph without a stored box takes. */
function boxOf(points) {
    if (points.length === 0)
        return { xMin: 0, yMin: 0, xMax: 0, yMax: 0 };
    const box = { xMin: Infinity, yMin: Infinity, xMax: -Infinity, yMax: -Infinity };
    let x = 0;
    let y = 0;
    for (const point of points) {
        x += point.dx;
        y += point.dy;
        box.xMin = Math.min(box.xMin, x);
        box.yMin = Math.min(box.yMin, y);
        box.xMax = Math.max(box.xMax, x);
        box.yMax = Math.max(box.yMax, y);
    }
    return box;
}
function writeHeader(glyph, contourCount, box) {
    glyph.u16(contourCount);
    for (const edge of [box.xMin, box.yMin, box.xMax, box.yMax])
        glyph.u16(edge);
}
const fitsInByte = (delta) => Math.abs(delta) <= MAX_SHORT_DELTA;
/** The deltas of one axis, each as a byte, a word, or nothing for a delta of 0. */
function writeAxis(glyph, deltas) {
    for (const delta of deltas) {
        if (delta === 0)
            continue;
        if (fitsInByte(delta))
            glyph.u8(Math.abs(delta));
        else
            glyph.u16(delta);
    }
}
/** The flag bits of one axis: a zero delta repeats, a short one carries its sign, a long one is a word. */
function axisFlag(delta, shortBit, positiveBit) {
    if (delta === 0)
        return positiveBit;
    if (fitsInByte(delta))
        return shortBit | (delta > 0 ? positiveBit : 0);
    return 0;
}
function writeSimpleGlyph(glyph, contourEnds, points, instructions) {
    for (const end of contourEnds)
        glyph.u16(end);
    glyph.u16(instructions.length);
    glyph.append(instructions);
    for (const { dx, dy, isOnCurve } of points) {
        glyph.u8((isOnCurve ? SIMPLE_ON_CURVE : 0) |
            axisFlag(dx, SIMPLE_X_SHORT, SIMPLE_X_SAME_OR_POSITIVE) |
            axisFlag(dy, SIMPLE_Y_SHORT, SIMPLE_Y_SAME_OR_POSITIVE));
    }
    writeAxis(glyph, points.map((point) => point.dx));
    writeAxis(glyph, points.map((point) => point.dy));
}
/** Copies the component records of a composite glyph, up to the one without `MORE_COMPONENTS`, and tells whether instructions follow. */
function copyComponents(composite, glyph) {
    let hasInstructions = false;
    let flags = COMPONENT_MORE;
    while (flags & COMPONENT_MORE) {
        flags = composite.u16();
        glyph.u16(flags);
        glyph.append(composite.take(GLYPH_INDEX_BYTES + componentArgumentByteCount(flags) + componentMatrixByteCount(flags)));
        hasInstructions ||= (flags & COMPONENT_HAS_INSTRUCTIONS) !== 0;
    }
    return hasInstructions;
}
function readStoredBox(boxes) {
    return { xMin: boxes.i16(), yMin: boxes.i16(), xMax: boxes.i16(), yMax: boxes.i16() };
}
function simpleGlyph(contourCount, storedBox, streams) {
    const contourEnds = [];
    let pointCount = 0;
    for (let contour = 0; contour < contourCount; contour++) {
        pointCount += read255UInt16(streams.pointCounts);
        contourEnds.push(pointCount - 1);
    }
    const points = readPoints(pointCount, streams.flags, streams.glyphStream);
    const instructionLength = read255UInt16(streams.glyphStream);
    const glyph = new GlyphBytes();
    writeHeader(glyph, contourCount, storedBox ?? boxOf(points));
    writeSimpleGlyph(glyph, contourEnds, points, streams.instructions.take(instructionLength));
    return glyph.toBytes();
}
function compositeGlyph(box, streams) {
    const glyph = new GlyphBytes();
    writeHeader(glyph, COMPOSITE_CONTOUR_COUNT, box);
    if (copyComponents(streams.composites, glyph)) {
        const instructions = streams.instructions.take(read255UInt16(streams.glyphStream));
        glyph.u16(instructions.length);
        glyph.append(instructions);
    }
    return glyph.toBytes();
}
/** The glyphs whose box the font stores: the bit of glyph `index` in the bitmap, most significant bit first. */
function hasStoredBox(bitmap, index) {
    return (bitmap[index >> 3] & (0x80 >> (index & 7))) !== 0;
}
function glyphAt(index, bitmap, streams) {
    const contourCount = streams.contourCounts.i16();
    const isBoxStored = hasStoredBox(bitmap, index);
    if (contourCount === 0) {
        if (isBoxStored)
            throw new Error(`expected glyph ${index} with no contours to have no stored box`);
        return new Uint8Array(0);
    }
    if (contourCount === COMPOSITE_CONTOUR_COUNT) {
        if (!isBoxStored)
            throw new Error(`expected composite glyph ${index} to have a stored box`);
        return compositeGlyph(readStoredBox(streams.boxes), streams);
    }
    if (contourCount < 0)
        throw new Error(`expected glyph ${index} to have -1 or more contours, got ${contourCount}`);
    return simpleGlyph(contourCount, isBoxStored ? readStoredBox(streams.boxes) : null, streams);
}
/** The glyphs laid end to end, each padded to `alignment`, and the offset each starts at, with the end last. */
function joinGlyphs(glyphs, alignment) {
    const padded = glyphs.map((glyph) => Math.ceil(glyph.length / alignment) * alignment);
    const offsets = [0];
    for (const length of padded)
        offsets.push(offsets[offsets.length - 1] + length);
    const glyf = new Uint8Array(offsets[offsets.length - 1]);
    glyphs.forEach((glyph, index) => glyf.set(glyph, offsets[index]));
    return { glyf, offsets };
}
function locaOf(offsets, format) {
    const long = format === LONG_LOCA_FORMAT;
    if (!long && offsets[offsets.length - 1] > MAX_SHORT_LOCA_OFFSET) {
        throw new Error(`expected glyph data within ${MAX_SHORT_LOCA_OFFSET} bytes for a short loca, got ${offsets[offsets.length - 1]}`);
    }
    const loca = new DataView(new ArrayBuffer(offsets.length * (long ? 4 : 2)));
    offsets.forEach((offset, index) => {
        if (long)
            loca.setUint32(index * 4, offset, false);
        else
            loca.setUint16(index * 2, offset / 2, false);
    });
    return new Uint8Array(loca.buffer);
}
/** Throws on a table that does not hold the streams its header counts, or a glyph the streams do not describe. */
export function rebuildGlyfTables(transformed) {
    const header = ByteReader.bigEndian(transformed);
    header.u16(); // reserved
    header.u16(); // optionFlags: bit 0 adds an overlap bitmap after the streams, which no reader here needs
    const glyphCount = header.u16();
    const locaFormat = header.u16();
    if (locaFormat !== SHORT_LOCA_FORMAT && locaFormat !== LONG_LOCA_FORMAT) {
        throw new Error(`expected an indexFormat of 0 or 1, got ${locaFormat}`);
    }
    const sizes = Array.from({ length: HEADER_STREAM_COUNT }, () => header.u32());
    const [contourCounts, pointCounts, flags, glyphStream, composites, boxes, instructions] = sizes.map((size) => ByteReader.bigEndian(header.take(size)));
    // The bitmap sits at the start of the box stream, one bit for each glyph, in whole four-byte words.
    const bitmap = boxes.take(((glyphCount + 31) >> 5) << 2);
    const streams = {
        contourCounts,
        pointCounts,
        flags,
        glyphStream,
        composites,
        boxes,
        instructions,
    };
    const glyphs = Array.from({ length: glyphCount }, (_, index) => glyphAt(index, bitmap, streams));
    const { glyf, offsets } = joinGlyphs(glyphs, locaFormat === LONG_LOCA_FORMAT ? LONG_GLYPH_ALIGNMENT : SHORT_GLYPH_ALIGNMENT);
    return { glyf, loca: locaOf(offsets, locaFormat), xMins: Int16Array.from(glyphs, glyphXMin) };
}
//# sourceMappingURL=woff2Glyf.js.map